import { useState } from 'react'
import { useMutation, type QueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, patchJSON, postJSON } from '@/lib/api'

export type EmailProfile = {
  email: string
  pending_email_change?: { email: string; requested_at: string }
}

type ProfileResponse = { profile: EmailProfile }
type ProfileErrors = Record<string, string[]>
export type EmailStep = 'entering' | 'verifying'

export function useMemberPortalEmailChange(
  current: EmailProfile,
  queryClient: QueryClient,
  onConfirmed: () => void,
) {
  const [step, setStep] = useState<EmailStep>('entering')
  const [newEmail, setNewEmail] = useState('')
  const [code, setCode] = useState('')
  const [errors, setErrors] = useState<ProfileErrors>({})
  const reset = () => {
    setStep('entering')
    setNewEmail('')
    setCode('')
    setErrors({})
  }
  const onError = (error: ApiError) => {
    const body = error.body as { errors?: ProfileErrors } | undefined
    setErrors(body?.errors ?? {})
  }
  const request = useMutation({
    mutationFn: () =>
      patchJSON<ProfileResponse>('/api/v1/member_portal/profile', {
        profile: { email: newEmail },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['member-portal-profile'], data)
      setStep('verifying')
      setErrors({})
    },
    onError,
  })
  const confirm = useMutation({
    mutationFn: () =>
      postJSON<ProfileResponse>('/api/v1/member_portal/profile/email_change_confirmation', {
        email_change: { email: newEmail, code },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['member-portal-profile'], data)
      reset()
      onConfirmed()
    },
    onError,
  })
  return {
    step,
    newEmail,
    code,
    errors,
    pending: request.isPending || confirm.isPending,
    begin: () => {
      setNewEmail(current.email)
      setCode('')
      setStep('entering')
      setErrors({})
    },
    reset,
    setNewEmail,
    setCode,
    send: () => request.mutate(),
    verify: () => confirm.mutate(),
    verified: confirm.isSuccess,
  }
}

export function MemberPortalEmailChange({
  current,
  editing,
  flow,
  onEdit,
  onCancel,
}: {
  current: EmailProfile
  editing: boolean
  flow: ReturnType<typeof useMemberPortalEmailChange>
  onEdit: () => void
  onCancel: () => void
}) {
  return (
    <section className="border-t border-border first:border-t-0">
      <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6">
        <h2 className="text-body font-medium text-foreground">Email &amp; sign-in</h2>
        {!editing && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Edit Email & sign-in"
            onClick={onEdit}
          >
            <Pencil className="size-4" aria-hidden="true" />
          </Button>
        )}
      </div>
      <div className="px-5 pb-5 sm:px-6 sm:pb-6">
        {!editing ? (
          <ReadOnlyEmail current={current} />
        ) : (
          <EmailEditor current={current} flow={flow} onCancel={onCancel} />
        )}
      </div>
    </section>
  )
}

function ReadOnlyEmail({ current }: { current: EmailProfile }) {
  return (
    <>
      <EmailRow label="Email" value={current.email} />
      <p className="pt-1 text-caption text-muted-foreground">
        Signing in uses a code sent to this address.
      </p>
      {current.pending_email_change && (
        <p className="pt-2 text-caption text-muted-foreground">
          A verification code was sent to {current.pending_email_change.email}.
        </p>
      )}
    </>
  )
}

function EmailEditor({
  current,
  flow,
  onCancel,
}: {
  current: EmailProfile
  flow: ReturnType<typeof useMemberPortalEmailChange>
  onCancel: () => void
}) {
  if (flow.step === 'entering') {
    return (
      <>
        <EmailRow label="Current email" value={current.email} />
        <div className="py-2.5">
          <Label
            htmlFor="profile-new-email"
            className="text-body-sm font-normal text-muted-foreground"
          >
            New email
          </Label>
          <Input
            id="profile-new-email"
            type="email"
            className="mt-1 h-9"
            value={flow.newEmail}
            onChange={(event) => flow.setNewEmail(event.target.value)}
            disabled={flow.pending}
          />
          <ErrorMessages messages={flow.errors.email} />
        </div>
        <Actions
          primary={flow.pending ? 'Sending...' : 'Send code'}
          onPrimary={flow.send}
          onCancel={onCancel}
          disabled={flow.pending}
        />
      </>
    )
  }
  return (
    <div className="space-y-4">
      <EmailRow label="Current email" value={current.email} />
      <p className="text-caption text-muted-foreground">
        We sent a code to {flow.newEmail}. It expires in 10 minutes.
      </p>
      <div className="space-y-1">
        <Label
          htmlFor="profile-email-code"
          className="text-body-sm font-normal text-muted-foreground"
        >
          Sign-in Code
        </Label>
        <Input
          id="profile-email-code"
          inputMode="numeric"
          maxLength={6}
          className="h-9 max-w-48 tracking-[0.3em]"
          value={flow.code}
          onChange={(event) => flow.setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          disabled={flow.pending}
        />
        <ErrorMessages messages={flow.errors.email_change} />
      </div>
      <Actions
        primary={flow.pending ? 'Verifying...' : 'Verify'}
        onPrimary={flow.verify}
        onCancel={onCancel}
        disabled={flow.pending || flow.code.length !== 6}
      />
    </div>
  )
}

function EmailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-2.5">
      <div className="flex flex-col gap-1 sm:flex-row sm:gap-4">
        <Label className="w-full shrink-0 text-body-sm font-normal text-muted-foreground sm:w-40">
          {label}
        </Label>
        <div className="min-w-0 flex-1 break-words text-body-sm text-foreground">{value}</div>
      </div>
    </div>
  )
}

function ErrorMessages({ messages }: { messages?: string[] }) {
  return (
    <>
      {messages?.map((message) => (
        <Alert key={message} variant="destructive" className="mt-1 py-1 text-body-sm">
          {message}
        </Alert>
      ))}
    </>
  )
}

function Actions({
  primary,
  onPrimary,
  onCancel,
  disabled,
}: {
  primary: string
  onPrimary: () => void
  onCancel: () => void
  disabled: boolean
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-4">
      <Button type="button" size="sm" onClick={onPrimary} disabled={disabled}>
        {primary}
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={disabled}>
        Cancel
      </Button>
    </div>
  )
}
